<template>
	<CDialog
		:visible="visible"
		custom-class="wallet-refill-dialog dialog--wide"
		@done="$emit('close')"
	>
		<div v-if="loading" class="wallet-refill-dialog__loader flex justify-center py-5">
			<Inline svg="loading" class="spin" />
		</div>
		<div v-else class="wallet-refill-dialog__body b10:flex gap-5 -mx-5 -mb-5">
			<div class="b10:w-2/3 px-5 pb-5">
				<nav class="wallet-refill-dialog__nav mt-5 mb-5 mx-5">
					<ol class="flex items-center gap-3 b4:gap-4 text-2 b5:text-3 list-none b4:list-decimal">
						<li :class="{ 'font-bold': step === 'customize', 'text-link': step === 'checkout' }">
							<button v-if="step === 'checkout'" type="button" class="button button--no-style text-2 b5:text-3" @click="step = 'customize'">Customize Wallet</button>
							<template v-else>Customize Wallet</template>
						</li>
						<li :class="{ 'font-bold': step === 'checkout', 'text-link': step === 'customize' && canCheckout, 'text-disabled': step === 'customize' && !canCheckout }">
							<button v-if="step === 'customize' && canCheckout" type="button" class="button button--no-style text-2 b5:text-3" @click="goToCheckout">Check Out</button>
							<template v-else>Check Out</template>
						</li>
					</ol>
				</nav>
				<CWalletCustomizeSection
					v-show="step === 'customize'"
					:wallet-add-ons="walletAddOns"
					:quantities="quantities"
					:average-rows="averageRows"
					:usage-window-label="usageWindowLabel"
					:plan="plan"
					:current-plan-name="currentPlanName"
					@update-quantity="onUpdateQuantity"
				/>
				<section v-show="step === 'checkout'">
					<CCheckoutBilling
						v-if="step === 'checkout'"
						ref="checkoutBilling"
						:plan="currentPlanName"
						:annual-commitments="annualCommitments"
						:on-submit="handleWalletSubmit"
						@loading="checkoutLoading = $event"
						@cost-updated="costModel = $event"
						@cost-loading="costLoading = $event"
						@cost-throttled="costThrottled = $event"
						@change-plan-status="submitStatus = $event"
						@handle-close="step = 'customize'"
					/>
				</section>
			</div>
			<aside class="change-plan-sidebar bg-shade-2 b10:w-1/3 p-4 pt-5 flex flex-col">
				<h2 class="font-display text-4 mb-4">Usage Wallet Details</h2>
				<div class="mb-4 bg-primary p-3 border">
					<div class="flex justify-between items-center py-2">
						<span>Current Balance</span>
						<span>{{ formatUSD(currentWalletBalance) }}</span>
					</div>
					<div v-show="amountToAdd > 0" class="flex justify-between items-center py-2">
						<span>Amount to Add</span>
						<span>{{ formatUSD(amountToAdd) }}</span>
					</div>
					<div v-show="amountToAdd > 0" class="flex justify-between items-center py-2 font-bold">
						<span>New Balance</span>
						<span>{{ formatUSD(currentWalletBalance + amountToAdd) }}</span>
					</div>
				</div>
				<CCallout v-if="step === 'customize' && amountToAdd <= 0" type="info" class="mb-4">
					Based on your selections, your current usage wallet balance will cover these projected costs.
				</CCallout>
				<CCallout v-else-if="step === 'customize' && belowMinimumTopUp" type="warning" class="mb-4">
					Usage wallet top-ups must be at least {{ formatUSD(MINIMUM_TOP_UP) }}. Add at least {{ formatUSD(MINIMUM_TOP_UP - amountToAdd) }} more to continue.
				</CCallout>
				<CCallout v-else-if="costError" type="error" class="mb-4">
					We are unable to load a cost preview at the moment. Please try again later.
				</CCallout>
				<div v-else-if="costBusy" class="flex justify-center py-5 mb-4">
					<Inline svg="loading" class="spin" />
				</div>
				<table v-else-if="invoice" class="w-full mb-4" :class="{ 'opacity-50': costDimmed }">
					<tbody>
						<tr v-for="(item, index) in invoice.LineItems.filter(li => li.Amount !== 0)" :key="index" class="border-b">
							<th class="py-3 pr-3 text-left font-normal w-full">{{ item.Description }}</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(item.Amount) }}</td>
						</tr>
					</tbody>
					<tfoot>
						<tr>
							<th class="py-3 pr-3 text-right w-full">Subtotal:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(invoice.Subtotal) }}</td>
						</tr>
						<tr v-if="invoice.BalanceApplied < 0">
							<th class="py-3 pr-3 text-right w-full">Credits Applied:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(invoice.BalanceApplied) }}</td>
						</tr>
						<tr v-if="invoice.TaxAmount !== 0 || invoice.TaxDisplayText">
							<th class="py-3 pr-3 text-right w-full font-normal">{{ invoice.TaxDisplayText || 'Sales Tax' }}:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(invoice.TaxAmount) }}</td>
						</tr>
						<tr>
							<th class="py-3 pr-3 text-right w-full text-3">Amount Due:</th>
							<td class="py-3 text-right whitespace-nowrap"><strong>{{ formatCurrencyAccounting(invoice.AmountDue) }}</strong></td>
						</tr>
					</tfoot>
				</table>
				<div v-if="step === 'customize' && canCheckout" class="flex justify-end mt-auto">
					<button
						type="button"
						class="button button--primary"
						@click="goToCheckout"
					>
						Next
					</button>
				</div>
				<StatusButton
					v-else-if="step === 'checkout'"
					class="button--primary w-full"
					:status="costBusy || costThrottled || !invoice ? 'disabled' : submitStatus"
					@click="payNow"
					@reset-button-status="submitStatus = 'default'"
				>
					Pay Now
				</StatusButton>
			</aside>
		</div>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, ref, reactive, computed, watch, type PropType } from 'vue';
	import type { PricingTier } from '@cognitoforms/types/server-types/plans/model/pricing-tier';
	import type { UsageAnnualAverageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-annual-average-view-model';
	import type { UsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-view-model';
	import { createWalletRefill, refillWallet } from '@cognitoforms/api/services/subscription-service';
	import type { SubscriptionCostViewModel } from '@cognitoforms/api/services/subscription-service';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import { getOrganizationUsageAnnualAverage, getOrganizationUsage } from '@cognitoforms/api/services/settings/organization-settings-service';

	import { formatCurrencyAccounting, formatUSD } from 'src/utilities/currency';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CCheckoutBilling from './CheckoutBilling.vue';
	import CWalletCustomizeSection from './WalletCustomizeSection.vue';
	import { useWalletAddOns, type WalletQuantities } from './use-wallet-addons';
	import type { AnnualCommitments } from './use-checkout-billing';
	import CCallout from 'src/components/content/Callout.vue';
	import StatusButton from 'src/components/interaction/StatusButton.vue';

	const MONTHS_PER_YEAR = 12;
	const MINIMUM_TOP_UP = 1;

	// The subset of the invoice the cost sidebar renders. The customize step builds this on
	// the client; the checkout step's full server invoice satisfies the same shape.
	interface PreviewInvoice {
		LineItems: { Description: string; Amount: number }[];
		Subtotal: number;
		BalanceApplied: number;
		TaxAmount: number;
		TaxDisplayText: string;
		AmountDue: number;
	}

	export default defineComponent({
		name: 'CWalletRefillDialog',
		components: { CDialog, CCheckoutBilling, CWalletCustomizeSection, CCallout, StatusButton },
		props: {
			visible: { type: Boolean, default: false },
			pricingTier: { type: Object as PropType<PricingTier | null>, default: null },
			currentPlanName: { type: String, default: '' },
			currentWalletBalance: { type: Number, default: 0 }
		},
		emits: ['close', 'refilled'],
		setup(props, { emit }) {
			const loading = ref(true);
			const submitStatus = ref<ButtonStatus>('default');

			const step = ref<'customize' | 'checkout'>('customize');
			const checkoutBilling = ref<InstanceType<typeof CCheckoutBilling> | null>(null);
			const checkoutLoading = ref(false);
			const costLoading = ref(false);
			const costThrottled = ref(false);

			const usageAverages = ref<UsageAnnualAverageViewModel | null>(null);
			const currentUsage = ref<UsageViewModel | null>(null);
			const submissionToken = ref<string | null>(null);
			const costModel = ref<SubscriptionCostViewModel | null>(null);
			const annualInvoicesLeft = ref(MONTHS_PER_YEAR);

			const quantities = reactive<WalletQuantities>({
				guests: 0,
				documentGeneration: 0,
				storage: 0,
				apiUsage: 0,
				additionalFunds: 0
			});

			const plan = computed(() => props.pricingTier?.Plans?.find(p => p.Name === props.currentPlanName) ?? null);

			const {
				suggestedUsage,
				walletAddOns,
				averageRows,
				usageWindowLabel,
				walletTotal
			} = useWalletAddOns({
				plan,
				currentUsage,
				usageAverages,
				quantities,
				multiplier: annualInvoicesLeft,
				withWarnings: true
			});

			const amountToAdd = computed(() => Math.max(0, walletTotal.value - props.currentWalletBalance));
			const belowMinimumTopUp = computed(() => amountToAdd.value > 0 && amountToAdd.value < MINIMUM_TOP_UP);
			const canCheckout = computed(() => amountToAdd.value >= MINIMUM_TOP_UP);

			// The customize step prices the wallet top-up entirely on the client: a single
			// prepayment line item with no tax (a prepayment invoice never applies a balance,
			// and tax can only be resolved server-side). The checkout step instead shows the
			// official tax-inclusive invoice owned by the embedded billing form.
			const customizeInvoice = computed<PreviewInvoice | null>(() => amountToAdd.value > 0
				? {
					LineItems: [{ Description: 'Usage Wallet', Amount: amountToAdd.value }],
					Subtotal: amountToAdd.value,
					BalanceApplied: 0,
					TaxAmount: 0,
					TaxDisplayText: '',
					AmountDue: amountToAdd.value
				}
				: null);
			const invoice = computed<PreviewInvoice | null>(() => step.value === 'checkout'
				? costModel.value?.invoice ?? null
				: customizeInvoice.value);

			// On the checkout step the embedded billing form owns the (tax-inclusive) cost. Only
			// fall back to a spinner when there is nothing to show yet; once a preview is on
			// screen, keep it and just dim it while the checkout cost refreshes so moving to
			// billing doesn't flash a reload. The customize step never loads or errors because
			// its preview is computed locally.
			const checkoutCostLoading = computed(() => checkoutLoading.value || costLoading.value);
			const costBusy = computed(() => step.value === 'checkout' && checkoutCostLoading.value && !invoice.value);
			const costDimmed = computed(() => step.value === 'checkout' && checkoutCostLoading.value && !!invoice.value);
			const costError = computed(() => step.value === 'checkout'
				&& (costThrottled.value || (!checkoutCostLoading.value && !invoice.value)));

			const annualCommitments = computed<AnnualCommitments>(() => ({ isAnnual: true, users: null, wallet: amountToAdd.value }));

			watch(suggestedUsage, s => {
				quantities.guests = s.guests;
				quantities.documentGeneration = s.documents;
				quantities.storage = s.storageGb;
				quantities.apiUsage = s.apiUsage;
			});

			const resetState = () => {
				quantities.guests = 0;
				quantities.documentGeneration = 0;
				quantities.storage = 0;
				quantities.apiUsage = 0;
				quantities.additionalFunds = 0;
				costModel.value = null;
				submitStatus.value = 'default';
				step.value = 'customize';
				checkoutLoading.value = false;
				costLoading.value = false;
				costThrottled.value = false;
			};

			watch(() => props.visible, async (val) => {
				if (!val) {
					resetState();
					return;
				}
				loading.value = true;
				try {
					const [averages, usage, walletSettings] = await Promise.all([
						getOrganizationUsageAnnualAverage(),
						getOrganizationUsage(),
						createWalletRefill()
					]);
					usageAverages.value = averages;
					currentUsage.value = usage;
					submissionToken.value = walletSettings.SubmissionToken;
					annualInvoicesLeft.value = walletSettings.RemainingAnnualRevenueInvoiceCount ?? MONTHS_PER_YEAR;
				}
				finally {
					loading.value = false;
				}
			});

			const goToCheckout = () => {
				if (!canCheckout.value)
					return;
				checkoutLoading.value = true;
				step.value = 'checkout';
			};

			const payNow = () => checkoutBilling.value?.submit();

			function onUpdateQuantity({ model, value }: { model: keyof WalletQuantities; value: number }) {
				quantities[model] = value;
			}

			// Invoked by the embedded billing form once billing info is saved. The card is now
			// on file, so the wallet top-up charges against it and we close in place.
			const handleWalletSubmit = async () => {
				if (!submissionToken.value || !canCheckout.value)
					throw new Error('Unable to add funds to your usage wallet.');
				await refillWallet(amountToAdd.value, submissionToken.value);
				emit('refilled');
				emit('close');
			};

			return {
				loading,
				submitStatus,
				quantities,
				plan,
				walletAddOns,
				usageWindowLabel,
				averageRows,
				walletTotal,
				amountToAdd,
				belowMinimumTopUp,
				canCheckout,
				MINIMUM_TOP_UP,
				invoice,
				formatCurrencyAccounting,
				formatUSD,
				step,
				checkoutBilling,
				costModel,
				checkoutLoading,
				costLoading,
				costThrottled,
				costBusy,
				costDimmed,
				costError,
				annualCommitments,
				goToCheckout,
				payNow,
				onUpdateQuantity,
				handleWalletSubmit
			};
		}
	});
</script>

<style lang="scss">
.wallet-refill-dialog {

	&__loader {
		min-height: 50dvh;
	}

	&__body {
		margin-top: calc(var(--spacing-5) * -1);

		@include b-up($b4) {
			margin-top: -48px;
		}

		section {
			padding: 0 var(--spacing-5);

			@include b-up($b9) {
				padding: 0 var(--spacing-6);
			}

			@include b-up($b10) {
				padding: 0 var(--spacing-5);
			}

			@include b-up($b12) {
				padding: 0 var(--spacing-6);
			}
		}
	}

	&__nav {

		li:not(:last-child)::after {
			display: inline-block;
			width: var(--spacing-icon);
			height: var(--spacing-icon);
			background: url('~@cognitoforms/shared-components/src/assets/graphics/icons/chevron-right.svg') no-repeat center;
			background-size: contain;
			margin: 0 0 0 var(--spacing-3);
			vertical-align: text-bottom;
			content: '';

			@include b-up($b4) {
				margin: 0 var(--spacing-3);
			}
		}
	}
}
</style>
